// src/preview/cards.ts
// Text cards and Kindle pages for the browser draft renderer, made from the
// same HTML templates make_video renders with
// (FbAdPipeline/templates/video/*.html, imported at build time).
//
// make_video loads each card page in headless Edge, runs fit.js and takes a
// transparent screenshot. A page can't screenshot itself, so here:
//   1. the template is filled with the same tokens (card_tokens/kindle_tokens
//      are ported below), with the Google Fonts <link> swapped for the same
//      font files inlined as data: URLs (see scripts/fetch_card_fonts.py);
//   2. it's laid out in a hidden sandboxed iframe (no scripts run in it) and
//      fitBlock, a port of fit.js, shrinks the text from out here;
//   3. the laid-out document is serialised as XHTML into an SVG
//      <foreignObject> and drawn to a canvas. SVG images load nothing
//      external and run no script, which is why the fonts are inlined.
// ─────────────────────────────────────────────────────────────────

import fontManifest from './fonts/manifest.json';
import { DEFAULT_IMAGE_SCALE, FLEX, KINDLE_ASPECT, findPhrase, imageBox, pyRound, type Json } from './spec';

const templateFiles = import.meta.glob('../../../FbAdPipeline/templates/video/*.html', {
  query: '?raw', import: 'default', eager: true,
}) as Record<string, string>;
const fontUrls = import.meta.glob('./fonts/*.woff2', { query: '?url', import: 'default', eager: true }) as Record<string, string>;

/** Template name (file stem) → its HTML. */
export const TEMPLATES: Record<string, string> = Object.fromEntries(
  Object.entries(templateFiles).map(([path, html]) => [path.replace(/^.*\/(.+)\.html$/, '$1'), html]),
);

export type Tokens = Record<string, string>;

interface FontFace { family: string; style: string; weight: string; unicodeRange: string; file: string }
const FONTS = fontManifest as Record<string, FontFace[]>;

/** Python's html.escape(s, quote=True). */
function htmlEscape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
}

/** Port of make_video.card_tokens: the template tokens for one text box. `spec` has DEFAULTS merged in. */
export function cardTokens(beat: Json, spec: Json, w: number, h: number): { tokens: Tokens; clampedY: number | null } {
  const st = spec.style;
  const yPct: number = beat.y ?? st.y;
  const anchor: string = beat.anchor ?? st.anchor;
  // Reels/Stories overlay UI at the top and bottom; keep text clear of it on tall canvases
  const tall = h / w > 1.5;
  let safeTop: number, safeBottom: number;
  if (st.safeZone && tall) {
    safeTop = pyRound(h * 0.14);
    safeBottom = pyRound(h * 0.20);
  } else {
    safeTop = safeBottom = pyRound(h * 0.06);
  }
  const y = pyRound(h * yPct / 100);
  const room = anchor !== 'middle' ? 0 : pyRound(h * 0.05);
  const clamped = Math.min(Math.max(y, safeTop + room), h - safeBottom - room);
  let padTop: number, padBottom: number;
  if (anchor === 'top') {
    padTop = clamped; padBottom = safeBottom;
  } else if (anchor === 'bottom') {
    padTop = safeTop; padBottom = h - clamped;
  } else {
    const half = Math.min(clamped - safeTop, h - safeBottom - clamped);
    padTop = clamped - half; padBottom = h - clamped - half;
  }
  const padSide = pyRound(w * 0.08);
  const font = beat.fontSize || st.fontSize;
  const auto = font === 'auto';
  return {
    clampedY: clamped !== y ? pyRound(clamped * 100 / h) : null,
    tokens: {
      W: String(w), H: String(h),
      TEXT: String(beat.text).replace(/\n/g, '<br>'),
      BEAT_CSS: htmlEscape(beat.css ?? ''),
      FONT_SIZE: String(auto ? pyRound(w * 0.095) : pyRound(Math.trunc(Number(font)) * w / 1080)),
      MIN_FONT: String(Math.max(8, pyRound(20 * w / 1080))),
      AUTO_FIT: auto ? '1' : '0',
      COLOR: st.color, ACCENT: st.accent,
      WIDTH: String(beat.width ?? st.width),
      TEXT_STYLE: beat.textStyle ?? st.textStyle,
      PILL_COLOR: beat.pillColor ?? st.pillColor,
      EMPHASIS_CLASS: beat.emphasis ? 'emphasis' : '',
      JUSTIFY: FLEX[anchor],
      PAD_TOP: String(padTop), PAD_BOTTOM: String(padBottom),
      PAD_LEFT: String(padSide), PAD_RIGHT: String(padSide),
    },
  };
}

/**
 * Port of make_video.kindle_tokens: tokens for a Kindle page of w x h px.
 * `highlight` phrases (first occurrence of each) are wrapped in <mark> here.
 */
export function kindleTokens(k: Json, w: number, h: number): Tokens {
  let text: string = k.text;
  const spans: [number, number, string][] = [];
  for (const hl of k.highlight ?? []) {
    const [phrase, colour] = typeof hl === 'string' ? [hl, 'yellow'] : [hl.text, hl.color ?? 'yellow'];
    const i = findPhrase(text, phrase);
    if (i >= 0) spans.push([i, i + phrase.length, colour]);
  }
  let out = '', pos = 0;
  for (const [a, b, colour] of spans.sort((x, y) => x[0] - y[0] || x[1] - y[1])) {
    if (a < pos) continue; // overlaps an earlier highlight
    out += text.slice(pos, a) + `<mark class="${colour}">${text.slice(a, b)}</mark>`;
    pos = b;
  }
  text = out + text.slice(pos);

  const font = k.fontSize ?? 'auto';
  const auto = font === 'auto';
  const side = pyRound(w * 0.08);
  return {
    W: String(w), H: String(h),
    TEXT: text.replace(/\n/g, '<br>'),
    TITLE: htmlEscape(k.title ?? ''),
    THEME: k.theme ?? 'light',
    FONT_SIZE: String(auto ? pyRound(w * 0.052) : pyRound(Math.trunc(Number(font)) * w / 1080)),
    MIN_FONT: String(Math.max(8, pyRound(20 * w / 1080))),
    AUTO_FIT: auto ? '1' : '0',
    PAD_TOP: String(pyRound(h * 0.09)), PAD_BOTTOM: String(pyRound(h * 0.06)),
    PAD_LEFT: String(side), PAD_RIGHT: String(side),
  };
}

/** Size a Kindle page is rendered at, as make_video's resolve_kindles works it out: the size it's shown at. */
export function kindleSize(im: Json, w: number, h: number): [number, number] {
  let [kw] = imageBox(w, h, im.scale ?? DEFAULT_IMAGE_SCALE);
  const boxH = imageBox(w, h, im.scale ?? DEFAULT_IMAGE_SCALE)[1];
  const aspect: number = im.kindle.aspect ?? KINDLE_ASPECT;
  let kh = pyRound(kw * aspect);
  if (kh > boxH) {
    kh = boxH;
    kw = pyRound(boxH / aspect);
  }
  return [kw, kh];
}

/** make_video's render_card_page substitution: every {{NAME}}, then any token left over becomes "". */
export function fillTemplate(template: string, tokens: Tokens): string {
  let html = template;
  for (const [k, v] of Object.entries(tokens)) html = html.split(`{{${k}}}`).join(v);
  return html.replace(/\{\{[A-Z_]+\}\}/g, '');
}

// ── fonts ────────────────────────────────────────────────────────

const fontData = new Map<string, Promise<string>>();

function fontDataUrl(file: string): Promise<string> {
  let p = fontData.get(file);
  if (!p) {
    const url = fontUrls[`./fonts/${file}`];
    if (!url) throw new Error(`Font ${file} is missing; rerun scripts/fetch_card_fonts.py`);
    p = fetch(url)
      .then(r => { if (!r.ok) throw new Error(`Font ${file}: HTTP ${r.status}`); return r.blob(); })
      .then(blob => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(new Blob([blob], { type: 'font/woff2' }));
      }));
    p.catch(() => fontData.delete(file)); // don't remember a failed download
    fontData.set(file, p);
  }
  return p;
}

function inRanges(cp: number, unicodeRange: string): boolean {
  return unicodeRange.split(',').some(part => {
    const [a, b] = part.trim().replace(/^U\+/i, '').split('-');
    const lo = parseInt(a, 16);
    return cp >= lo && cp <= (b ? parseInt(b, 16) : lo);
  });
}

/**
 * @font-face rules for a template, with the files inlined. Only the
 * unicode-range subsets the text actually uses are included (the browser
 * would only download those too), which keeps each card's SVG small.
 */
async function fontFaceCss(templateName: string, text: string): Promise<{ css: string; faces: number }> {
  const cps = [...new Set([...text].map(c => c.codePointAt(0)!))];
  const faces = (FONTS[templateName] ?? []).filter(f => cps.some(cp => inRanges(cp, f.unicodeRange)));
  const rules = await Promise.all(faces.map(async f => `@font-face { font-family: '${f.family}'; font-style: ${f.style}; `
    + `font-weight: ${f.weight}; src: url(${await fontDataUrl(f.file)}) format('woff2'); unicode-range: ${f.unicodeRange}; }`));
  return { css: rules.join('\n'), faces: rules.length };
}

const FONT_LINK = /<link href="https:\/\/fonts\.googleapis\.com\/[^"]*" rel="stylesheet">/;
const CSP_FONT_SRC = 'font-src https://fonts.gstatic.com';

/** The filled template with its fonts inlined, ready to load anywhere, and how many font faces it declares. */
async function cardPage(templateName: string, tokens: Tokens): Promise<{ html: string; faces: number }> {
  const template = TEMPLATES[templateName];
  if (!template) throw new Error(`Unknown card template "${templateName}"`);
  if (!FONT_LINK.test(template) || !template.includes(CSP_FONT_SRC)) {
    // The rewrite below would silently fall back to system fonts
    throw new Error(`Template ${templateName}'s font link or CSP changed; update cards.ts`);
  }
  const html = fillTemplate(template, tokens);
  const { css, faces } = await fontFaceCss(templateName, Object.values(tokens).join(''));
  return { html: html.replace(FONT_LINK, () => `<style>${css}</style>`).replace(CSP_FONT_SRC, 'font-src data:'), faces };
}

// ── layout and rasterising ───────────────────────────────────────

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

/** Loads `html` into a hidden iframe that can't run script, at w x h. Remove it when done. */
async function layoutFrame(html: string, w: number, h: number): Promise<HTMLIFrameElement> {
  const frame = document.createElement('iframe');
  // allow-same-origin only, so this page can measure it; without allow-scripts nothing in it runs
  frame.setAttribute('sandbox', 'allow-same-origin');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.style.cssText = `position:fixed;left:-${w + 100}px;top:0;width:${w}px;height:${h}px;border:0;visibility:hidden;pointer-events:none`;
  const loaded = new Promise<void>(resolve => frame.addEventListener('load', () => resolve(), { once: true }));
  frame.srcdoc = html;
  document.body.append(frame);
  await loaded;
  return frame;
}

/**
 * Port of templates/video/fit.js, run against the iframe from outside it.
 * Returns whether the fonts loaded. fit.js treats "no faces declared" as
 * the stylesheet failing to load; here no faces can also mean the text
 * uses no character the template's fonts cover (emoji only, say), which
 * isn't a failure: `expectFaces` tells the two apart.
 */
async function fitBlock(doc: Document, auto: boolean, minFont: number, expectFaces: boolean): Promise<boolean> {
  const win = doc.defaultView!;
  doc.body.getBoundingClientRect(); // lay out, so the fonts the text uses start loading
  await Promise.race([doc.fonts.ready, sleep(10000)]);
  const card = doc.getElementById('card')!;
  const el = doc.getElementById('text')!;
  if (auto) {
    const cs = win.getComputedStyle(card);
    const maxH = card.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    let size = parseFloat(win.getComputedStyle(el).fontSize);
    // shrink until the block fits the padded box and no word overflows its line
    while ((el.scrollHeight > maxH || el.scrollWidth > el.clientWidth) && size > minFont) {
      size -= 2;
      el.style.fontSize = size + 'px';
    }
  }
  const faces = [...doc.fonts];
  if (faces.length === 0) return !expectFaces;
  return !faces.some(f => f.status === 'error');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = src;
  return img.decode().then(() => img);
}

export interface Card {
  /** Just the card's visible pixels (a 1x1 blank canvas if there are none). */
  canvas: HTMLCanvasElement;
  /** Where `canvas` sits on the w x h page; w and h are 0 if nothing is visible. */
  rect: { x: number; y: number; w: number; h: number };
  fontsOk: boolean;
}

/** The bounding box of every pixel with any opacity, or null if there are none. */
function opaqueBounds(ctx: CanvasRenderingContext2D, w: number, h: number): Card['rect'] | null {
  const data = ctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w * 4;
    for (let x = 0; x < w; x++) {
      if (data[row + x * 4 + 3] === 0) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      y1 = y;
    }
  }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/**
 * Renders one card page (text card or Kindle page) at w x h, then crops it
 * to its visible pixels: a text card is mostly transparent, and the cache
 * holds many of them.
 */
export async function rasterise(templateName: string, tokens: Tokens, w: number, h: number): Promise<Card> {
  const page = await cardPage(templateName, tokens);
  const frame = await layoutFrame(page.html, w, h);
  let svg: string, fontsOk: boolean;
  try {
    const doc = frame.contentDocument!;
    fontsOk = await fitBlock(doc, tokens.AUTO_FIT === '1', Number(tokens.MIN_FONT), page.faces > 0);
    doc.querySelector('meta[http-equiv]')?.remove(); // inert in an SVG image anyway
    const xhtml = new XMLSerializer().serializeToString(doc.documentElement);
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">`
      + `<foreignObject x="0" y="0" width="${w}" height="${h}">${xhtml}</foreignObject></svg>`;
  } finally {
    frame.remove();
  }
  const img = await loadImage('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg));
  const full = document.createElement('canvas');
  full.width = w;
  full.height = h;
  const ctx = full.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, w, h);
  const bounds = opaqueBounds(ctx, w, h);
  const canvas = document.createElement('canvas');
  canvas.width = bounds?.w ?? 1;
  canvas.height = bounds?.h ?? 1;
  if (bounds) canvas.getContext('2d')!.drawImage(full, bounds.x, bounds.y, bounds.w, bounds.h, 0, 0, bounds.w, bounds.h);
  return { canvas, rect: bounds ?? { x: 0, y: 0, w: 0, h: 0 }, fontsOk };
}
